import { AlertTriangle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { MappingStatus, SablonSurum, VariableMapping } from '../types'

type Filter = 'all' | MappingStatus

export function VariableMappingPanel({
  surum,
  onChange,
}: {
  surum: SablonSurum
  onChange: (mappings: VariableMapping[]) => void
}) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<Filter>('all')
  const mappings = useMemo(() => surum.variableMappings ?? [], [surum.variableMappings])

  const counts = useMemo(() => {
    return {
      all: mappings.length,
      eslesti: mappings.filter((m) => m.status === 'eslesti').length,
      manuel: mappings.filter((m) => m.status === 'manuel').length,
      tanimsiz: mappings.filter((m) => m.status === 'tanimsiz').length,
    }
  }, [mappings])

  const rows = mappings.filter((m) => (filter === 'all' ? true : m.status === filter))
  const hasErrors = counts.tanimsiz > 0

  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-card p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t('hr.belgeler.mapping.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('hr.belgeler.mapping.subtitle')}</p>
        </div>
        {surum.demoVariables?.length ? (
          <Badge tone="warning">{t('hr.belgeler.mapping.demoBadge')}</Badge>
        ) : null}
      </div>

      {hasErrors ? (
        <div className="flex gap-2 rounded-xl border border-destructive/25 bg-destructive/8 px-3 py-2.5 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            {t('hr.belgeler.mapping.errorBanner', {
              undefined: counts.tanimsiz,
              missing: mappings.filter((m) => m.status === 'manuel' && !m.mappedFieldKey).length,
            })}
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1">
        {(
          [
            ['all', counts.all],
            ['eslesti', counts.eslesti],
            ['manuel', counts.manuel],
            ['tanimsiz', counts.tanimsiz],
          ] as const
        ).map(([key, n]) => (
          <Button
            key={key}
            type="button"
            size="sm"
            variant={filter === key ? 'default' : 'outline'}
            className={cn(filter !== key && 'bg-card')}
            onClick={() => setFilter(key)}
          >
            {t(`hr.belgeler.mapping.filter.${key}`)} ({n})
          </Button>
        ))}
      </div>

      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>{t('hr.belgeler.mapping.templateVar')}</TH>
            <TH>{t('hr.belgeler.mapping.mapped')}</TH>
            <TH>{t('hr.belgeler.mapping.status')}</TH>
            <TH className="w-28">{t('hr.belgeler.mapping.actions')}</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((m) => (
            <TR key={m.templateVar}>
              <TD className="font-mono text-xs">{`{{${m.templateVar}}}`}</TD>
              <TD className="text-sm text-muted-foreground">{m.mappedFieldKey ?? '—'}</TD>
              <TD>
                <Badge
                  tone={
                    m.status === 'eslesti' ? 'success' : m.status === 'manuel' ? 'info' : 'danger'
                  }
                >
                  {t(`hr.belgeler.mapping.statusLabel.${m.status}`)}
                </Badge>
              </TD>
              <TD>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={m.status === 'eslesti'}
                  onClick={() => {
                    const next = mappings.map((row) =>
                      row.templateVar === m.templateVar
                        ? {
                            ...row,
                            status: 'manuel' as const,
                            mappedFieldKey: row.mappedFieldKey ?? row.templateVar.split('.').pop() ?? null,
                            note: undefined,
                          }
                        : row,
                    )
                    onChange(next)
                  }}
                >
                  {t('hr.belgeler.mapping.edit')}
                </Button>
              </TD>
            </TR>
          ))}
          {!rows.length ? (
            <TR>
              <TD colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                {t('hr.belgeler.mapping.empty')}
              </TD>
            </TR>
          ) : null}
        </TBody>
      </Table>

      {mappings
        .filter((m) => m.status === 'tanimsiz' && m.note)
        .map((m) => (
          <div
            key={`note-${m.templateVar}`}
            className="rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-foreground"
          >
            {m.note}
          </div>
        ))}
    </div>
  )
}
